<template>
  <div class="tooltip-container relative flex w-auto justify-center">
    <Teleport to="body" :disabled="!teleport">
      <div
        ref="tooltipRef"
        class="tooltip-popup flex items-center justify-center"
        :class="[tooltipClass, !placement && 'hidden']"
        :style="popupStyle"
      >
        <div
          class="absolute h-2 w-2 rotate-45 bg-black ds:bg-(--ds-tooltip-bg)"
          :class="{
            // horizontal placements
            '-left-1 top-1/2 -translate-y-1/2':
              currentPlacement?.includes('right'),
            '-right-1 top-1/2 -translate-y-1/2':
              currentPlacement?.includes('left'),

            // vertical placements
            '-top-1 left-1/2 -translate-x-1/2':
              currentPlacement?.includes('bottom'),
            '-bottom-1 left-1/2 -translate-x-1/2':
              currentPlacement?.includes('top'),
          }"
        ></div>

        <!-- Design-system look: the tooltip tokens (tooltip.*) of the design system; as wide as its
             text up to tooltip.maxWidth instead of the fixed width of the old look. -->
        <div
          class="z-5 relative rounded bg-black py-[5px] px-2 ds:rounded-(--ds-tooltip-radius) ds:bg-(--ds-tooltip-bg) ds:px-(--ds-tooltip-padding-x) ds:py-(--ds-tooltip-padding-y) ds:shadow-(--ds-tooltip-shadow)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="text-tiny font-medium text-white text-center whitespace-break-spaces ds:max-w-(--ds-tooltip-max-width) ds:text-(length:--ds-tooltip-font-size) ds:leading-(--ds-tooltip-line-height) ds:text-(--ds-tooltip-text)"
            :class="designSystem ? 'w-max text-left' : width"
            v-html="description || $t('common.some_information_about')"
          />
        </div>
      </div>
    </Teleport>

    <div
      ref="triggerButtonRef"
      class="w-full leading-none"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
    >
      <slot>
        <!-- Design-system look: one info badge everywhere, in the info colour (callers' old
             icon colours only apply to the old look). -->
        <InfoBadge v-if="designSystem" :size="iconSize" class="cursor-help" />
        <FontAwesomeIcon
          v-else
          :icon="['fas', 'info-circle']"
          class="inline-block cursor-pointer"
          :style="{
            color: resolvedIconColor,
            fontSize: iconSize,
          }"
        />
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import InfoBadge from '@/Components/common/InfoBadge.vue'
import { useDesignSystem } from '@/composables/designSystem'

const props = defineProps({
  description: {
    type: [String, null],
    required: false,
    default: () => null,
  },
  iconColor: {
    type: [String, null],
    default: null,
  },
  iconSize: {
    type: String,
    default: '14px',
  },
  width: {
    type: String,
    default: 'w-[180px]',
  },
  tooltipClass: {
    type: String,
    default: '',
  },
  placement: {
    type: String,
    default: 'top', // 'top' | 'bottom' | 'left' | 'right' \ '' set empty to hide
  },
  // Render the popup in <body> so it can never be clipped by an ancestor's
  // overflow (e.g. inside a scrollable dropdown). When enabled, visibility is
  // driven by JS hover instead of the CSS `.tooltip-container:hover` rule,
  // which no longer reaches the teleported popup.
  teleport: {
    type: Boolean,
    default: false,
  },
})

const designSystem = useDesignSystem()

/**
 * The info icon's colour: the caller's, else the old look's pink, or in the design-system look
 * the design system's info colour (status.info).
 */
const resolvedIconColor = computed(
  () =>
    props.iconColor ??
    (designSystem.value ? 'var(--ds-status-info-text)' : '#c547d3'),
)

const triggerButtonRef = ref(null)
const tooltipRef = ref(null)
// The side the popup ended up on (after flipping at the viewport edge); a ref so the arrow
// follows it on the first open too, which the element's dataset alone would not trigger.
const currentPlacement = ref(props.placement)
const position = ref({ top: 0, left: 0 })
const isOpen = ref(false)

const popupStyle = computed(() => ({
  position: 'fixed',
  top: `${position.value.top}px`,
  left: `${position.value.left}px`,
  ...(props.teleport
    ? { visibility: isOpen.value ? 'visible' : 'hidden' }
    : {}),
}))

const onEnter = () => {
  isOpen.value = true
  positionTooltip()
}

const onLeave = () => {
  isOpen.value = false
}

const positionTooltip = async () => {
  await nextTick()

  const button = triggerButtonRef.value
  const tooltip = tooltipRef.value

  if (!button || !tooltip) return

  const buttonRect = button.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const gap = 8
  const padding = 8

  let placement = props.placement

  const calcPosition = (side) => {
    switch (side) {
      case 'top':
        return {
          top: buttonRect.top - tooltipRect.height - gap,
          left: buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2,
        }
      case 'bottom':
        return {
          top: buttonRect.bottom + gap,
          left: buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2,
        }
      case 'left':
        return {
          top: buttonRect.top + buttonRect.height / 2 - tooltipRect.height / 2,
          left: buttonRect.left - tooltipRect.width - gap,
        }
      case 'right':
        return {
          top: buttonRect.top + buttonRect.height / 2 - tooltipRect.height / 2,
          left: buttonRect.right + gap,
        }
      default:
        return { top: 0, left: 0 }
    }
  }

  let { top: tryTop, left: tryLeft } = calcPosition(placement)

  const fitsTop = tryTop >= padding
  const fitsBottom = tryTop + tooltipRect.height <= viewportHeight - padding
  const fitsLeft = tryLeft >= padding
  const fitsRight = tryLeft + tooltipRect.width <= viewportWidth - padding

  // flip placement if already on browser edges
  if (placement === 'top' && !fitsTop) placement = 'bottom'
  else if (placement === 'bottom' && !fitsBottom) placement = 'top'
  else if (placement === 'left' && !fitsLeft) placement = 'right'
  else if (placement === 'right' && !fitsRight) placement = 'left'

  const { top: finalTop, left: finalLeft } = calcPosition(placement)

  const clampedLeft = Math.min(
    Math.max(finalLeft, padding),
    viewportWidth - tooltipRect.width - padding,
  )
  const clampedTop = Math.min(
    Math.max(finalTop, padding),
    viewportHeight - tooltipRect.height - padding,
  )

  tooltip.dataset.placement = placement
  currentPlacement.value = placement
  position.value = { top: clampedTop, left: clampedLeft }
}

watch(isOpen, (open) => {
  if (open) positionTooltip()
})

onMounted(() => {
  window.addEventListener('scroll', positionTooltip, true)
  window.addEventListener('resize', positionTooltip)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', positionTooltip, true)
  window.removeEventListener('resize', positionTooltip)
})
</script>
